import React, { useState, useEffect, useRef } from 'react';
import { UserCircleIcon } from '@heroicons/react/outline';
import { motion } from 'framer-motion';

function ProfileTab({ user, setUser, setMessage }) {
  const [profile, setProfile] = useState({ username: '', email: '', full_name: '', profile_picture_url: '' });
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      setProfile({
        username: user.username || '',
        email: user.email || '',
        full_name: user.full_name || '',
        profile_picture_url: user.profile_picture_url || ''
      });
    }
  }, [user]);

  const handleProfileSave = e => {
    e.preventDefault();
    fetch('/api/user.php?action=save_profile', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(profile),
      credentials: 'include'
    })
      .then(res => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(() => {
        setMessage('Profile updated');
        setError('');
        setUser(prev => ({ ...prev, ...profile }));
      })
      .catch(() => setError('Failed'));
  };

  const handlePasswordChange = e => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) {
      setError('Passwords do not match');
      return;
    }
    if (passwords.new.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    fetch('/api/user.php?action=change_password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ current_password: passwords.current, new_password: passwords.new }),
      credentials: 'include'
    })
      .then(async res => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || 'Password change failed');
        }
        return data;
      })
      .then(() => {
        setMessage('Password changed');
        setPasswords({ current: '', new: '', confirm: '' });
        setError('');
      })
      .catch(err => setError(err.message));
  };

  const fileInput = useRef(null);

  const handleAvatar = e => {
    const file = e.target.files[0];
    if (!file) return;
    const form = new FormData();
    form.append('avatar', file);
    fetch('/api/user.php?action=upload_avatar', { method: 'POST', body: form, credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setProfile(p => ({ ...p, profile_picture_url: data.path }));
        setUser(prev => ({ ...prev, profile_picture_url: data.path }));
      });
  };

  const triggerUpload = () => fileInput.current && fileInput.current.click();

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-gray-800">
      {error && <div className="mb-2 text-red-600">{error}</div>}

      <div className="flex flex-col items-center mb-6">
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          onChange={handleAvatar}
          className="hidden"
        />
        {profile.profile_picture_url ? (
          <img
            src={profile.profile_picture_url}
            alt="avatar"
            onClick={triggerUpload}
            className="w-32 h-32 rounded-full object-cover cursor-pointer hover:opacity-80 transition"
          />
        ) : (
          <UserCircleIcon
            onClick={triggerUpload}
            className="w-32 h-32 text-gray-300 cursor-pointer hover:text-gray-400 transition"
          />
        )}
      </div>

      <form onSubmit={handleProfileSave} className="space-y-4 max-w-md mx-auto">
        <div>
          <label className="block text-sm font-medium">Full Name</label>
          <input
            value={profile.full_name}
            onChange={e => setProfile({ ...profile, full_name: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium">Email</label>
          <input
            value={profile.email}
            onChange={e => setProfile({ ...profile, email: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium">Username</label>
          <input
            value={profile.username}
            onChange={e => setProfile({ ...profile, username: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
        >
          Save Profile
        </button>
      </form>

      <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md mx-auto mt-8">
        <h3 className="font-semibold">Change Password</h3>
        <div>
          <label className="block text-sm font-medium">Current Password</label>
          <input
            type="password"
            value={passwords.current}
            onChange={e => setPasswords({ ...passwords, current: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium">New Password</label>
          <input
            type="password"
            value={passwords.new}
            onChange={e => setPasswords({ ...passwords, new: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium">Confirm New Password</label>
          <input
            type="password"
            value={passwords.confirm}
            onChange={e => setPasswords({ ...passwords, confirm: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
        >
          Change Password
        </button>
      </form>
    </motion.div>
  );
}

export default ProfileTab;

